import { useReportTokenGenerate } from '@/core/domain/reports/reportToken/reportToken.hooks';
import { ReportTokenType } from '@/core/domain/reports/reportToken/reportToken.types';
import { Button, notification } from 'antd';
import QRCode from 'react-qr-code';
import { renderToStaticMarkup } from 'react-dom/server';

interface Props {
  showQrCode?: boolean;
  selectedIndex?: number[];
  complementIdentifier?: string;
  previewRefs?: React.MutableRefObject<Record<string, HTMLDivElement | null>>;
  infosRequest: {
    complementId?: number;
    licensePlate?: string;
    companyId?: number;
    showLastServices?: boolean;
  };
}

export default function PrintButton(props: Props) {
  const reportTokenGenerate = useReportTokenGenerate();

  const handlePrint = async () => {
    if (!props.selectedIndex?.length) {
      return notification.warning({
        message: 'Selecione ao menos uma etiqueta',
      });
    }

    let qrCodeSvg: string | undefined;

    if (props.showQrCode) {
      const tokenData = await reportTokenGenerate.mutateAsync({
        params: JSON.stringify(props.infosRequest),
        reportType: ReportTokenType.VEHICLE_STICKER,
      });

      const qrCodeUrlWithToken = `${window.location.origin}/r/reviewEtiquette/${tokenData.token}`;

      qrCodeSvg = renderToStaticMarkup(
        <QRCode value={qrCodeUrlWithToken} size={90} />,
      );
    }

    const etiquettesHtml = props.selectedIndex
      ?.map((id) => {
        const element = props.previewRefs?.current[id];
        if (!element) return '';

        const clone = element.cloneNode(true) as HTMLElement;
        clone.classList.remove('selected');
        clone.style.margin = '0 auto 16px';

        const qrCodeContainer = clone.querySelector('.etiquette-review-qrcode');

        if (qrCodeContainer && qrCodeSvg) {
          qrCodeContainer.innerHTML = qrCodeSvg;
        }

        return clone.outerHTML;
      })
      .join('');

    const styles = Array.from(document.styleSheets)
      .map((sheet) => {
        try {
          return Array.from(sheet.cssRules)
            .filter((rule) => {
              if (rule instanceof CSSMediaRule) {
                return !rule.media.mediaText.includes('print');
              }
              return true;
            })
            .map((rule) => rule.cssText)
            .join('\n');
        } catch {}
      })
      .join('\n');

    const printWindow = window.open('', '_blank', 'width=800,height=600');
    if (!printWindow) return;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>Etiquetas - OS #${props.complementIdentifier}</title>
          <style>
            ${styles}
            * { box-sizing: border-box; }
            body {
              margin: 0;
              padding: 16px;
              background: #fff;
              display: flex;
              flex-direction: column;
              align-items: center;
              gap: 16px;
            }
            @media print {
              @page { margin: 8mm; }
              body { padding: 0; gap: 8mm; }
              .etiquette { page-break-inside: avoid; break-inside: avoid; }
            }
          </style>
        </head>
        <body>
          ${etiquettesHtml}
        </body>
      </html>
    `);

    printWindow.document.close();

    printWindow.onload = () => {
      printWindow.focus();
      printWindow.print();
      printWindow.close();
    };
  };

  return (
    <Button
      type="primary"
      onClick={handlePrint}
      loading={reportTokenGenerate.isLoading}
    >
      Imprimir etiquetas selecionadas
    </Button>
  );
}
